/**
 * Section "L'événement en images" — intro du slider 5 images vedettes.
 *
 * Rendu côté serveur. Même style visuel que la section `lieu` originale
 * (cta obscura-centered) : eyebrow or, titre italic, body cream, trait or final.
 *
 * Placée entre la section "Le Lieu" (slider 7 images du CNA) et le slider 5
 * images vedettes éditable via CRM.
 */

import { ou } from '@/lib/contenuEditable'

/** Contenu éditable depuis le CRM — section `evenement-en-images` de la home. */
export interface EventHighlightsIntroContent {
  eyebrow?: string
  title?: string
  /** Les deux paragraphes, séparés par une ligne vide — même convention que
      les autres blocs de texte du site. */
  subtitle?: string
}

const PARAGRAPHES_ORIGINE =
  "Quelques fragments capturés lors de cette soirée d'exception, entre la scène contemporaine et raffinements d'Asie.\n\nUne nuit, cinq tableaux choisis pour témoigner d'un moment unique."

export default function EventHighlightsIntro({
  content,
}: {
  content?: EventHighlightsIntroContent
}) {
  const eyebrow = ou(content?.eyebrow, "L'événement en images")
  const titre = ou(content?.title, 'Cinq instants de la nuit')
  const paragraphes = ou(content?.subtitle, PARAGRAPHES_ORIGINE)
    .split('\n\n')
    .filter((p) => p.trim() !== '')

  return (
    <section
      id="evenement-en-images"
      className="bg-brand-cream text-center scroll-mt-20"
    >
      <div className="max-w-4xl mx-auto px-4 md:px-10 py-20 md:py-28">
        <p
          className="text-brand-ink/70 uppercase font-medium mb-6"
          style={{
            fontFamily: 'var(--font-body, inherit)',
            fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)',
            letterSpacing: '0.4em',
          }}
        >
          {eyebrow}
        </p>

        <h2
          className="italic font-normal text-brand-ink/95 leading-tight mb-10"
          style={{
            fontFamily: 'var(--font-heading, inherit)',
            fontWeight: 400,
            fontSize: 'clamp(1.8rem, 1.2rem + 2.5vw, 3rem)',
            letterSpacing: '-0.01em',
          }}
        >
          {titre}
        </h2>

        <div
          className="space-y-5 text-brand-ink/80 leading-relaxed max-w-2xl mx-auto"
          style={{
            fontFamily: 'var(--font-body, inherit)',
            fontSize: 'clamp(0.95rem, 0.9rem + 0.15vw, 1.05rem)',
            lineHeight: 1.75,
          }}
        >
          {paragraphes.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="w-8 h-px bg-brand-ink/20 mx-auto mt-12" aria-hidden />
      </div>
    </section>
  )
}
